/**
 * GuestOrderMergeModal - 비회원 주문 병합 모달
 *
 * 로그인 후 저장된 비회원 credentials가 있을 때 표시됩니다.
 * 사용자가 확인하면 비회원 주문을 현재 계정에 병합합니다.
 */
import { useLanguage } from '@/contexts/AppContext';
import { useState, useCallback } from 'react';
import { Loader2, Package, CheckCircle, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { orderApi } from '@/lib/api/orders';

const MERGE_TEXT = {
  ko: {
    phoneLabel: '전화번호:',
    linking: '주문을 연결하는 중...',
    linked: '연결 완료!',
    linkFailed: '연결 실패',
  },
  es: {
    phoneLabel: 'Teléfono:',
    linking: 'Vinculando pedidos...',
    linked: '¡Vinculación completada!',
    linkFailed: 'Vinculación fallida',
  },
  en: {
    phoneLabel: 'Phone:',
    linking: 'Linking orders...',
    linked: 'Linked!',
    linkFailed: 'Link failed',
  },
} as const;

import { logger } from '@/utils/logger';

interface GuestOrderMergeModalProps {
  /** 모달 표시 여부 */
  open: boolean;
  /** 모달 닫기 핸들러 */
  onClose: () => void;
  /** 병합할 전화번호 */
  phone: string;
  /** 병합할 주문 조회 코드 */
  password: string;
  /** 병합 완료 후 콜백 */
  onMergeComplete?: (mergedCount: number) => void;
}

type MergeState = 'idle' | 'loading' | 'success' | 'error';

export function GuestOrderMergeModal({
  open,
  onClose,
  phone,
  password,
  onMergeComplete,
}: GuestOrderMergeModalProps) {
  const { language } = useLanguage();
  const m = MERGE_TEXT[language] ?? MERGE_TEXT.es;
  const [state, setState] = useState<MergeState>('idle');
  const [mergedCount, setMergedCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 병합 실행
  const handleMerge = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);

    try {
      const response = await orderApi.mergeGuestOrders({ phone, password });
      setMergedCount(response.merged_count);
      setState('success');
      onMergeComplete?.(response.merged_count);
    } catch (error) {
      logger.error('[GuestOrderMerge] Failed to merge orders:', error);
      setState('error');

      if (error instanceof Error) {
        // API 에러 메시지 표시
        if (error.message.includes('401') || error.message.includes('GUEST_AUTH_FAILED')) {
          setErrorMessage('주문 조회 코드가 올바르지 않습니다.');
        } else if (error.message.includes('429')) {
          setErrorMessage('너무 많은 요청이 발생했습니다. 잠시 후 다시 시도해주세요.');
        } else {
          setErrorMessage('주문 병합 중 오류가 발생했습니다.');
        }
      } else {
        setErrorMessage('알 수 없는 오류가 발생했습니다.');
      }
    }
  }, [phone, password, onMergeComplete]);

  // 모달 닫기 (상태 초기화)
  const handleClose = useCallback(() => {
    setState('idle');
    setMergedCount(0);
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  // 나중에 하기 (병합하지 않고 닫기)
  const handleSkip = useCallback(() => {
    handleClose();
  }, [handleClose]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            비회원 주문 연결
          </DialogTitle>
          <DialogDescription>
            이전에 비회원으로 주문한 내역이 있습니다.
            <br />
            회원 계정에 연결하시겠습니까?
          </DialogDescription>
        </DialogHeader>

        {/* 상태별 컨텐츠 */}
        <div className="py-4">
          {state === 'idle' && (
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                <span className="font-medium">{m.phoneLabel}</span>{' '}
                {phone.length >= 8
                  ? `${phone.slice(0, 4)}****${phone.slice(-2)}`
                  : phone.replace(/./g, '*')}
              </p>
              <p className="text-xs text-gray-500">
                연결하면 비회원 주문 내역을 회원 계정에서 확인할 수 있습니다.
              </p>
            </div>
          )}

          {state === 'loading' && (
            <div className="flex flex-col items-center py-4">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="mt-2 text-sm text-gray-600">{m.linking}</p>
            </div>
          )}

          {state === 'success' && (
            <div className="flex flex-col items-center py-4">
              <CheckCircle className="w-12 h-12 text-primary" />
              <p className="mt-2 text-lg font-semibold text-gray-800">{m.linked}</p>
              <p className="mt-1 text-sm text-gray-600">
                {mergedCount > 0
                  ? `${mergedCount}건의 주문이 회원 계정에 연결되었습니다.`
                  : '연결할 비회원 주문이 없습니다.'}
              </p>
            </div>
          )}

          {state === 'error' && (
            <div className="flex flex-col items-center py-4">
              <XCircle className="w-12 h-12 text-red-500" />
              <p className="mt-2 text-lg font-semibold text-gray-800">{m.linkFailed}</p>
              <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          {state === 'idle' && (
            <>
              <Button variant="outline" onClick={handleSkip} className="w-full sm:w-auto">
                나중에
              </Button>
              <Button
                onClick={handleMerge}
                className="w-full sm:w-auto bg-primary hover:bg-primary-hover"
              >
                연결하기
              </Button>
            </>
          )}

          {state === 'loading' && (
            <Button disabled className="w-full">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              처리 중...
            </Button>
          )}

          {state === 'success' && (
            <Button onClick={handleClose} className="w-full bg-primary hover:bg-primary-hover">
              확인
            </Button>
          )}

          {state === 'error' && (
            <>
              <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto">
                닫기
              </Button>
              <Button
                onClick={() => {
                  setState('idle');
                  setErrorMessage(null);
                }}
                className="w-full sm:w-auto bg-primary hover:bg-primary-hover"
              >
                다시 시도
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default GuestOrderMergeModal;
